import React from 'react';
import { format, processImagePath } from '../../../../../../../util/Functions';

import './ProposalDetails.scss';
import ProposalItemDetails from './ProposalItemDetails';
import Dialog from '../../../../../../../util/Dialog';
import LoadMask from "../../../../../../../components/LoadMask";
import Proposal from '../../../../../../../models/Proposal';

import { useAuth } from '../../../../../../../auth/useAuth';

export const Paths = {
	Proposal: {
		'*': true,
		items: {
			id: true,
			notes: true,
			quantity: true,
			price: true,
			updated_by_profile: ['id', 'given_name', 'family_name'],
			updated_on: true,
			created_by_profile: ['id', 'given_name', 'family_name'],
			created_on: true,
			proposal: true,
			item: {
				id: true,
				brand: { name: true, logo: true },
				description: true,
				image_0: true,
				image_1: true,
				retail_price: true,
				common_sku: true,
				style: true,
				name: true,
			},
			common_item: {
				brand: { name: true, logo: true },
				description: true,
				image_0: true,
				image_1: true,
				retail_price: true,
				common_sku: true,
				style: true,
				name: true,
			}
		},
		account: ['name', 'logo', 'code'],
		created_by_profile: ['id', 'given_name', 'family_name'],
		approved_by_profile: ['id', 'given_name', 'family_name'],
		currency: ['prefix', 'label'],
		shipping_method: ['label'],
		payment_method: ['label'],
	},
};

export default function ProposalDetails({ proposal, onComplete }) {
	const [processing, setProcessing] = React.useState(undefined);
	const { organizationMember } = useAuth();
	const organization_member_data = JSON.parse(localStorage.getItem('organization_member:data')) || organizationMember;
	const isAdmin = organization_member_data?.role?.is_administrator === true;
	const proposalCreatedByUser = proposal.created_by_profile?.id === organization_member_data?.profile?.id;
	
	if (!proposal) {
		return null;
	}

	const approve = async () => {
		setProcessing(true);
		const proposalCheckBeforeApproving = await Proposal.FetchBy({
			filter: { id: proposal.id, consented: true },
			paths: ["approved"],
		});
		setProcessing(false);
		const canApprove = proposalCheckBeforeApproving?.approved == null;
		
		if (canApprove) {
			const result = await Dialog.fire({
				title: 'Approve Proposal',
				text: "Are you sure you want to approve this proposal?",
				showCancelButton: true,
				confirmButtonText: 'Yes, approve it!',
				cancelButtonText: 'No'
			});
			
			if (result.isConfirmed) {
				setProcessing(true);
				proposal.setProperties({
					approved: true,
					approved_on: new Date().toISOString()
				});
				
				await proposal.save();
				onComplete?.();
				
				Dialog.fire({
					showConfirmButton: false,
					text: `Successfully submitted order`,
					icon: "success",
					timer: 1500,
				});
			}
		} else {
			let reasonMessage;
			
			if (proposalCheckBeforeApproving?.approved) {
				reasonMessage = <>It has already been approved.</>;
			}
			else if (proposalCheckBeforeApproving?.approved === false) {
				reasonMessage = <>It has already been declined.</>;
			}
			else {
				reasonMessage = <>
					It has been returned to an editable state.
					<br /><br />
					It will be available for approval again once it is resubmitted.
				</>;
			}
			
			Dialog.fire({
				html: (<>This Proposal cannot be approved. {reasonMessage}</>),
				icon: 'warning',
				confirmButtonText: 'OK',
			});
		}
	};

	const decline = async () => {
		const result = await Dialog.fire({
			title: 'Decline Proposal',
			text: "Are you sure you want to decline this proposal?",
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, decline it!',
			cancelButtonText: 'No'
		});

		if (result.isConfirmed) {
			setProcessing(true);
			proposal.setProperties({
				approved: false,
			});

			await proposal.save();
			onComplete?.();
			
			Dialog.fire(
				'Declined!',
				'The proposal has been declined.',
				'success'
			);
		}
	};
	
	const return_to_draft = async () => {
		const result = await Dialog.fire({
			title: 'Return to Draft?',
			html: "This will allow for edits to the Proposal.</br></br>It will also be removed from the approval queue until submitted again.</br></br>Continue?",
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, return to draft',
			cancelButtonText: 'No',
			customClass: {
				actions: 'swal-actions-return-to-draft',
				confirmButton: 'btn btn-primary',
				denyButton: 'btn btn-outline-secondary',
				cancelButton: 'btn btn-outline-secondary'
			}
		});

		if (result.isConfirmed) {
			setProcessing(true);
			proposal.setProperties({
				consented: false,
			});

			await proposal.save();
			onComplete?.();
		}
	};

	return (
		<div data-component="ProposalDetails">
			<LoadMask show={processing} />
			
			<div className="proposal-header">
				<div className="header-main">
					<h1>{proposal.name}</h1>
					<div className="meta">
						<span>
							Created: {format(proposal.created_on, { type: 'date' })}
							{proposal.created_by_profile && (
								<> by {proposal.created_by_profile.given_name} {proposal.created_by_profile.family_name}</>
							)}
						</span>
						{proposal.consented_on && <span>Consented: {format(proposal.consented_on, { type: 'date' })} by {proposal.consented_by_name}</span>}
						{proposal.customer_reference_number && <span>Ref: {proposal.customer_reference_number}</span>}
						{proposal.payment_method?.label && <span>Payment Method: {proposal.payment_method.label}</span>}
					</div>
				</div>
				<div className="header-aside">
					{proposal.account?.logo && <img src={processImagePath(proposal.account.logo)} alt={proposal.account.name} className="account-logo" />}
					<div className="actions">
						<a href={proposal.pdf} target="_blank" rel="noopener noreferrer" className="btn btn-icon" title="Download PDF">
							<i className="fal fa-file-pdf"></i>
						</a>
						<a href={proposal.xls} target="_blank" rel="noopener noreferrer" className="btn btn-icon" title="Download XLS">
							<i className="fal fa-file-excel"></i>
						</a>
					</div>
				</div>
			</div>


			<div className="approval-section">
				<h3>Approval Status</h3>
				<div className="approval-content">
					{!proposal.approved && !proposal.consented && (
						<div className="status-draft">
							<p>This proposal is a draft and has not been sent for approval.</p>
						</div>
					)}
					{proposal.consented && (
						<div className="status-info">
							<span className="label">Consented On</span>
							<span className="date">{format(proposal.consented_on, { type: 'date' })} by {proposal.consented_by_name}</span>
						</div>
					)}
					{/* approved/approved_on are set on save but the profile won't get set so this will throw js errors */}
					{proposal.approved && proposal.approved_on && proposal.approved_by_profile && (
						<div className="status-info">
							<span className="label">Approved On</span>
							<span className="date">{format(proposal.approved_on, { type: 'date' })} by {proposal.approved_by_profile.given_name} {proposal.approved_by_profile.family_name}</span>
						</div>
					)}
					
					{proposal.approved == null && proposal.consented && !proposal.declined && (isAdmin || proposalCreatedByUser) && (
						<div className="approval-actions">
							{isAdmin && <>
								<div
									className="btn btn-approve"
									onClick={approve}>
									Approve Proposal
								</div>
								<div
									className="btn btn-decline"
									onClick={decline}>
									Decline
								</div>
							</>}
							{proposalCreatedByUser && (
								<div
									className="btn btn-return-to-draft"
									onClick={return_to_draft}>
									Return to Draft
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			{proposal.notes && (
				<div className="proposal-notes">
					<h3>Notes</h3>
					<p>{proposal.notes}</p>
				</div>
			)}

			<div className="proposal-items">
				<h2>Items</h2>
				<div className="items">
					{proposal.items.map(item => (
						<ProposalItemDetails key={item.id} proposalItem={item} currency={proposal.currency} />
					))}
				</div>
			</div>

			<div className="proposal-summary">
				<div className="summary-item">
					<span>Total Units</span>
					<span>{format(proposal.units, { type: 'number' })}</span>
				</div>
				<div className="summary-item">
					<span>Subtotal</span>
					<span>{format(proposal.subtotal, { type: 'currency', currency: proposal.currency })}</span>
				</div>
			</div>
		</div>
	);
}
